import React, { useState } from "react";
import {
  Box,
  Button,
  Chip,
  Hidden,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableSortLabel,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import NoDataToBeShown from "../reusable/NoDataToBeShown";
import { User, User4Table } from "../users/users-types";
import { useTranslation } from "react-i18next";
import { GroupRounded } from "@mui/icons-material";
import { grey } from "@mui/material/colors";
import Edit from "@mui/icons-material/Edit";
import AddUser from "./AddUserDialog";
import { useUsers, useAllGroups } from "../../pages/api/api";
import ErrorComponent from "../reusable/ErrorComponent";

function UserStatus(props: { status: string }) {
  return props.status === "ADMIN" ? (
    <Box>
      <Chip
        label={props.status}
        variant="filled"
        color="info"
        sx={{ color: "white" }}
      />
    </Box>
  ) : props.status === "SUPERUSER" ? (
    <Box>
      <Chip
        label={props.status}
        variant="filled"
        color="success"
        sx={{ color: "white" }}
      />
    </Box>
  ) : props.status === "INACTIVE" ? (
    <Box>
      <Chip
        label={props.status}
        variant="filled"
        color="default"
        sx={{ color: "black" }}
      />
    </Box>
  ) : (
    <Box>
      <Chip
        label={props.status}
        variant="filled"
        color="primary"
        sx={{ color: "white" }}
      />
    </Box>
  );
}

function UserDetailsTable(props: {
  columns: string[];
  users: User[];
  isLoading: boolean;
  isError: any;
}) {
  const [orderDirection, setOrderDirection] = React.useState<Order>("asc");
  const [orderBy, setOrderBy] = React.useState("name");

  const { t } = useTranslation();
  const title = t("Edit");

  const userColumns = [t("Name"), t("Email"), t("Status")];
  const [openUpdateUserDialog, setOpenUpdateUserDialog] = useState(false);

  const { users, isLoadingUsers, isErrorUsers } = useUsers();
  const { allGroups, isLoadingAllGroups, isErrorAllGroups } = useAllGroups();
  const [selectedUser, setSelectedUser] = useState({} as User);

  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(5);
  const skeletonArray = Array(5).fill("");

  const handleOpenDialog = (user: User) => {
    setOpenUpdateUserDialog(true);
    setSelectedUser(user);
  };
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
    if (b[orderBy] < a[orderBy]) {
      return -1;
    }
    if (b[orderBy] > a[orderBy]) {
      return 1;
    }
    return 0;
  }

  type Order = "asc" | "desc";

  function getComparator<Key extends keyof any>(
    order: Order,
    orderBy: Key
  ): (
    a: { [key in Key]: number | string },
    b: { [key in Key]: number | string }
  ) => number {
    return order === "desc"
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  }

  function stableSort<T>(
    array: readonly T[],
    comparator: (a: T, b: T) => number
  ) {
    const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
    stabilizedThis.sort((a, b) => {
      const order = comparator(a[0], b[0]);
      if (order !== 0) {
        return order;
      }
      return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
  }

  const handleRequestSort = (property: keyof User) => {
    const isAsc = orderBy === property && orderDirection === "asc";
    setOrderDirection(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const users4Table: User4Table[] = props.users?.map((user) => {
    return {
      id: user.id,
      name: user.name,
      status: user.status,
    };
  });

  return (
    <Box>
      <AddUser
        users={users}
        groups={allGroups}
        update={true}
        selectedUser={selectedUser}
        open={openUpdateUserDialog}
        setOpen={setOpenUpdateUserDialog}
      />

      <TableContainer component={Paper}>
        <Table sx={{ minWidth: "100%" }} aria-label="simple table">
          <TableHead>
            <TableRow>
              <TableCell width="33%" align="center">
                <TableSortLabel
                  active={orderBy === "name"}
                  direction={orderBy === "name" ? orderDirection : "asc"}
                  onClick={() => handleRequestSort("name")}
                >
                  {userColumns[0]}
                </TableSortLabel>
              </TableCell>
              <Hidden mdDown>
                <TableCell width="33%" align="center">
                  {userColumns[1]}
                </TableCell>
              </Hidden>
              <TableCell width="33%" align="center">
                {userColumns[2]}
              </TableCell>
              {localStorage.getItem("Role") !== "USER" && (
                <TableCell width="33%" align="center" />
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {props.isLoading ? (
              skeletonArray.map((item, index) => (
                <TableRow
                  key={index}
                  sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                >
                  <TableCell component="th" scope="row" align="center">
                    <Skeleton />
                  </TableCell>
                  <Hidden smDown>
                    <TableCell align="center">
                      {" "}
                      <Skeleton />
                    </TableCell>
                  </Hidden>
                  <TableCell component="th" scope="row" align="center">
                    <Skeleton />
                  </TableCell>
                </TableRow>
              ))
            ) : props.users.length > 0 ? (
              stableSort(users4Table, getComparator(orderDirection, orderBy))
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((element: User) => (
                  <TableRow
                    key={element.id}
                    sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                  >
                    <TableCell
                      component="th"
                      align="center"
                      scope="row"
                      width="33%"
                    >
                      {element.name}
                    </TableCell>
                    <Hidden mdDown>
                      <TableCell
                        component="th"
                        align="center"
                        scope="row"
                        width="33%"
                      >
                        {element.id}
                      </TableCell>
                    </Hidden>
                    <TableCell align="center" scope="row" width="33%">
                      <UserStatus status={element.status} />
                    </TableCell>
                    {(localStorage.getItem("Role") === "ADMIN" &&
                      element.status !== "ADMIN" &&
                      element.status !== "INACTIVE") ||
                    (localStorage.getItem("Role") === "SUPERUSER" &&
                      element.status === "USER") ? (
                      <TableCell align="center" scope="row" width="33%">
                        <Tooltip title={title}>
                          <Button onClick={() => handleOpenDialog(element)}>
                            <Edit color="action" />
                          </Button>
                        </Tooltip>
                      </TableCell>
                    ) : (
                      <TableCell />
                    )}
                  </TableRow>
                ))
            ) : !props.isError && props.users.length > 0 ? (
              <TableRow>
                <TableCell colSpan={6}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      height: "30vh",
                      alignItems: "center",
                    }}
                  >
                    <Stack justifyContent="center" alignItems="center">
                      <GroupRounded sx={{ fontSize: 40, color: grey[500] }} />
                      <NoDataToBeShown />
                      <Typography textAlign="center">
                        {t("Add a user to the platform and assign him a role.")}
                      </Typography>
                    </Stack>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              props.isError && (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        height: "40vh",
                        alignItems: "center",
                      }}
                    >
                      <Stack justifyContent="center" alignItems="center">
                        <ErrorComponent />
                      </Stack>
                    </Box>
                  </TableCell>
                </TableRow>
              )
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        labelRowsPerPage={t("Rows per page")}
        rowsPerPageOptions={[5, 10, 25, 100]}
        component="div"
        count={props.users.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Box>
  );
}

export default UserDetailsTable;
